import cloneDeep from "lodash/cloneDeep";
import type { FormData as ApiFormData } from "@ifa/utils";
import type { CraPresentationVariant } from "./craVariant";
import {
  ZUU_CRA_FIELD_ALIASES,
  ZUU_CRA_FIELDS,
  ZUU_CRA_INPUT_FIELDS,
  ZUU_CRA_LEGACY_QUESTION_KEYS,
} from "./zuuCraFieldMap";

const RISK_ASSESSMENT_QUESTION_NUMBER_MAP: Record<string, string> = {
  pep: "1-1",
  fullUboAndDirectorList: "1-2",
  customerMediumRiskSituation: "1-3",
  customerHighRiskSituation: "1-4",
  validIdentityDocuments: "2-1",
  cooperative: "2-2",
  sourceOfFundsConsistent: "3-1",
  notHighRiskBusiness: "3-2",
  paidInitialPremium: "4-1",
  fromOwnAccount: "4-2",
  thirdPartyPayerRelation: "4-2",
  thirdPartyPayerInfo: "4-2",
  initialPremiumPaymentMethod: "4-3",
  referredByThirdParty: "5-1",
  instructionThroughThirdParty: "5-2",
  thirdPartyInstructionProof: "5-2",
  faceToFaceCommunication: "6-1",
  needsNonFaceToFaceSafeguards: "6-2",
  residentInHK: "7-1",
  nonHKResidenceCategory: "7-2",
  providesAnonymity: "8-1",
  involvesLargeCash: "8-2",
  frequentPurchasesWithoutReason: "8-3",
  supportsHighFrequencyIrregularActivity: "8-4",
  allowsLargeWithdrawals: "8-5",
  exceedsHighPremiumThreshold: "8-6",
};

const RISK_ASSESSMENT_INPUT_FIELDS = new Set([
  "thirdPartyPayerInfo",
  "thirdPartyInstructionProof",
]);
const RISK_ASSESSMENT_SELECT_FIELDS = new Set(["thirdPartyPayerRelation"]);
const RISK_ASSESSMENT_CHECKBOX_OPTION_KEYS = ["A", "B", "C", "D", "E", "F", "G"];
const RISK_ASSESSMENT_CHECKBOX_FIELDS = new Set([
  "initialPremiumPaymentMethod",
]);
const RISK_ASSESSMENT_CHECKBOX_OPTION_MAP: Record<string, string[]> = {
  initialPremiumPaymentMethod: RISK_ASSESSMENT_CHECKBOX_OPTION_KEYS,
};

const RISK_ASSESSMENT_RADIO_OPTION_MAP: Record<string, string[]> = {
  pep: ["A", "B"],
  customerMediumRiskSituation: ["A", "B"],
  customerHighRiskSituation: ["A", "B"],
  validIdentityDocuments: ["A", "B"],
  cooperative: ["A", "B"],
  sourceOfFundsConsistent: ["A", "B"],
  notHighRiskBusiness: ["A", "B"],
  paidInitialPremium: ["A", "B"],
  fromOwnAccount: ["A", "B"],
  involvesLargeCash: ["A", "B"],
  frequentPurchasesWithoutReason: ["A", "B"],
  supportsHighFrequencyIrregularActivity: ["A", "B"],
  allowsLargeWithdrawals: ["A", "B"],
  exceedsHighPremiumThreshold: ["A", "B"],
  providesAnonymity: ["A", "B"],
  faceToFaceCommunication: ["A", "B"],
  residentInHK: ["A", "B"],
  fullUboAndDirectorList: ["A", "B", "C"],
  referredByThirdParty: ["A", "B", "C"],
  instructionThroughThirdParty: ["A", "B", "C"],
  needsNonFaceToFaceSafeguards: ["A", "B", "C"],
  nonHKResidenceCategory: ["A", "B", "C"],
};

const ZUU_CRA_RADIO_OPTION_MAP: Record<string, string[]> = {
  hasVerifiedLatestIdentityDocument: ["A", "B"],
  customerResidenceRiskLevel: ["A", "B", "C"],
  customerIndustryRiskLevel: ["A", "B", "C"],
  customerFundsFromHongKong: ["A", "B"],
  initialPremiumPaymentMethod: ["A", "B", "C", "D", "E", "F"],
  renewalPremiumPaymentMethod: ["A", "B", "C", "D", "E", "F"],
  expectedAnnualTransactionCount: ["A", "B", "C"],
  qualifyingLifeInsurancePolicy: ["A", "B"],
  hasInsurableInterest: ["A", "B"],
  hasPepOrTerroristConnection: ["A", "B"],
};

const ZUU_CRA_CHECKBOX_OPTION_MAP: Record<string, string[]> = {
  customerSourceOfWealth: ["A", "B", "C", "D", "E", "F"],
};

const LEGACY_RISK_ASSESSMENT_FIELD_KEYS = Array.from(
  new Set([
    ...Object.keys(RISK_ASSESSMENT_QUESTION_NUMBER_MAP),
    ...Array.from(RISK_ASSESSMENT_INPUT_FIELDS),
    ...Array.from(RISK_ASSESSMENT_SELECT_FIELDS),
    ...Array.from(RISK_ASSESSMENT_CHECKBOX_FIELDS),
    ...Object.keys(RISK_ASSESSMENT_RADIO_OPTION_MAP),
  ]),
);

const ZUU_CRA_INPUT_FIELD_SET = new Set<string>(ZUU_CRA_INPUT_FIELDS as readonly string[]);
const ZUU_CRA_FIELD_SET = new Set<string>(ZUU_CRA_FIELDS as readonly string[]);
const SHARED_CRA_QUESTION_KEYS = new Set(["initialPremiumPaymentMethod"]);
const ZUU_CRA_EXCLUSIVE_FIELD_SET = new Set(
  ZUU_CRA_FIELDS.filter((key) => !SHARED_CRA_QUESTION_KEYS.has(key)),
);
const ZUU_CRA_INTERNAL_QUESTION_KEYS = new Set(
  Object.values(ZUU_CRA_FIELD_ALIASES)
    .flat()
    .filter((key) => key.startsWith("zuu")),
);

function ensureAnswerItem(answer: ApiFormData[], key: string) {
  let item = answer.find((question) => question.question === key);
  if (!item) {
    item = { question: key, items: [] };
    answer.push(item);
  }

  return item;
}

function applyLegacyAnswerValue(item: ApiFormData, key: string, rawValue: any) {
  if (RISK_ASSESSMENT_QUESTION_NUMBER_MAP[key]) {
    item.questionNumber = RISK_ASSESSMENT_QUESTION_NUMBER_MAP[key];
  }

  if (RISK_ASSESSMENT_INPUT_FIELDS.has(key) || RISK_ASSESSMENT_SELECT_FIELDS.has(key)) {
    const normalizedValue = rawValue ?? "";
    item.type = "INPUT";
    item.value = normalizedValue;
    item.items = [{ key: "A", value: normalizedValue }];
    return;
  }

  if (RISK_ASSESSMENT_CHECKBOX_FIELDS.has(key)) {
    const normalizedValue = Array.isArray(rawValue) ? rawValue : [];
    const optionKeys = RISK_ASSESSMENT_CHECKBOX_OPTION_MAP[key] || [];
    item.type = "CHECKBOX";
    item.value = normalizedValue;
    item.items = optionKeys.map((optionKey) => ({
      key: optionKey,
      value: normalizedValue.includes(optionKey),
    }));
    return;
  }

  const radioOptions = RISK_ASSESSMENT_RADIO_OPTION_MAP[key];
  if (radioOptions) {
    const normalizedValue = rawValue ?? null;
    item.type = "RADIO";
    item.value = normalizedValue;
    item.items = radioOptions.map((optionKey) => ({
      key: optionKey,
      value: normalizedValue === optionKey,
    }));
    return;
  }

  if (rawValue !== undefined) {
    item.value = rawValue;
  }
}

function applyZuuAnswerValue(item: ApiFormData, key: string, rawValue: any) {
  if (ZUU_CRA_INPUT_FIELD_SET.has(key)) {
    const normalizedValue = rawValue ?? "";
    item.questionNumber = null as any;
    item.type = "INPUT";
    item.value = normalizedValue;
    item.items = [{ key: "A", value: normalizedValue }];
    return;
  }

  const checkboxOptions = ZUU_CRA_CHECKBOX_OPTION_MAP[key];
  if (checkboxOptions) {
    const normalizedValue = Array.isArray(rawValue)
      ? rawValue.filter((value) => typeof value === "string" && value.trim().length > 0)
      : typeof rawValue === "string" && rawValue.trim().length > 0
        ? [rawValue]
        : [];
    item.questionNumber = null as any;
    item.type = "CHECKBOX";
    item.value = normalizedValue;
    item.items = checkboxOptions.map((optionKey) => ({
      key: optionKey,
      value: normalizedValue.includes(optionKey),
    }));
    return;
  }

  const radioOptions = ZUU_CRA_RADIO_OPTION_MAP[key] || [];
  const normalizedValue = rawValue ?? null;
  item.questionNumber = null as any;
  item.type = "RADIO";
  item.value = normalizedValue;
  item.items = radioOptions.map((optionKey) => ({
    key: optionKey,
    value: normalizedValue === optionKey,
  }));
}

export function buildLegacyRiskAssessmentAnswers(values: Record<string, any>, originalAnswer?: ApiFormData[]) {
  const nextAnswer = cloneDeep(originalAnswer || []).filter((question) => {
    const questionKey = question.question || "";
    return !ZUU_CRA_EXCLUSIVE_FIELD_SET.has(questionKey) && !ZUU_CRA_INTERNAL_QUESTION_KEYS.has(questionKey);
  });

  const fieldKeys = Array.from(
    new Set([
      ...LEGACY_RISK_ASSESSMENT_FIELD_KEYS,
      ...Object.keys(values).filter((key) => !ZUU_CRA_EXCLUSIVE_FIELD_SET.has(key) && !ZUU_CRA_INTERNAL_QUESTION_KEYS.has(key)),
    ]),
  );

  fieldKeys.forEach((key) => {
    const item = ensureAnswerItem(nextAnswer, key);
    applyLegacyAnswerValue(item, key, values[key]);
  });

  return nextAnswer;
}

export function buildZuuRiskAssessmentAnswers(values: Record<string, any>, originalAnswer?: ApiFormData[]) {
  const nextAnswer = cloneDeep(originalAnswer || []).filter((question) => {
    const questionKey = question.question || "";
    if (ZUU_CRA_LEGACY_QUESTION_KEYS.has(questionKey)) return false;
    return ZUU_CRA_FIELD_SET.has(questionKey);
  });

  const fieldKeys = Array.from(
    new Set([
      ...ZUU_CRA_FIELDS,
      ...Object.keys(values).filter((key) => ZUU_CRA_FIELD_SET.has(key)),
    ]),
  );

  fieldKeys.forEach((key) => {
    const item = ensureAnswerItem(nextAnswer, key);
    applyZuuAnswerValue(item, key, values[key]);
  });

  return nextAnswer;
}

export function buildRiskAssessmentAnswers(
  values: Record<string, any>,
  originalAnswer?: ApiFormData[],
  variant: CraPresentationVariant = "legacy",
) {
  if (variant === "zuu") {
    return buildZuuRiskAssessmentAnswers(values, originalAnswer);
  }

  return buildLegacyRiskAssessmentAnswers(values, originalAnswer);
}
